import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

// **Fetch Availabilities**
export const fetchAvailabilities = createAsyncThunk(
  'availability/fetchAvailabilities',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/appointments/availabilities/');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch availability'
      );
    }
  }
);

// **Add Availability**
export const addAvailability = createAsyncThunk(
  'availability/addAvailability',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/appointments/availabilities/',
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to add availability'
      );
    }
  }
);

// **Edit Availability**
export const updateAvailability = createAsyncThunk(
  'availability/updateAvailability',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/appointments/availabilities/${id}/`,
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to update availability'
      );
    }
  }
);

// **Delete Availability (Ensures No Booked Slots Exist)**
export const deleteAvailability = createAsyncThunk(
  'availability/deleteAvailability',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/appointments/availabilities/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to delete availability'
      );
    }
  }
);

// **Fetch Blocked Dates**
export const fetchBlockedDates = createAsyncThunk(
  'availability/fetchBlockedDates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/appointments/blocked-dates/');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to fetch blocked dates'
      );
    }
  }
);

// **Block a Date (Remove Unbooked Slots)**
export const addBlockedDate = createAsyncThunk(
  'availability/addBlockedDate',
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        '/appointments/blocked-dates/',
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to block date');
    }
  }
);

// **Delete a Blocked Date (Unblock for Booking)**
export const deleteBlockedDate = createAsyncThunk(
  'availability/deleteBlockedDate',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/appointments/blocked-dates/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || 'Failed to delete blocked date'
      );
    }
  }
);

const availabilitySlice = createSlice({
  name: 'availability',
  initialState: {
    availabilities: [],
    blockedDates: [],
    loading: false,
    error: null,
  },
  reducers: {
    resetAvailabilityState: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // **Fetch Availability**
      .addCase(fetchAvailabilities.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAvailabilities.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Handle both paginated and non-paginated responses
        const payload = action.payload;
        state.availabilities = Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload)
          ? payload
          : [];
      })
      .addCase(fetchAvailabilities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Failed to fetch availability';
      })

      // **Add Availability**
      .addCase(addAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Check if it already exists (avoid duplicates)
        const exists = state.availabilities.some(av => av.id === action.payload.id);
        if (!exists) {
          state.availabilities.push(action.payload);
        }
      })
      .addCase(addAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Failed to add availability';
      })

      // **Update Availability**
      .addCase(updateAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.availabilities.findIndex(
          (a) => a.id === action.payload.id
        );
        if (index !== -1) {
          state.availabilities[index] = action.payload;
        } else {
          // If not found, add it (shouldn't happen but handle gracefully)
          state.availabilities.push(action.payload);
        }
      })
      .addCase(updateAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Failed to update availability';
      })

      // **Delete Availability**
      .addCase(deleteAvailability.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAvailability.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.availabilities = state.availabilities.filter(
          (a) => a.id !== action.payload
        );
      })
      .addCase(deleteAvailability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.detail || action.payload?.error || action.payload || 'Failed to delete availability';
      })

      // **Fetch Blocked Dates**
      .addCase(fetchBlockedDates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchBlockedDates.fulfilled, (state, action) => {
        state.loading = false;
        state.blockedDates = Array.isArray(action.payload.results)
          ? action.payload.results
          : [];
      })
      .addCase(fetchBlockedDates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // **Add Blocked Date**
      .addCase(addBlockedDate.fulfilled, (state, action) => {
        state.blockedDates.push(action.payload);
      })

      // **Delete Blocked Date**
      .addCase(deleteBlockedDate.fulfilled, (state, action) => {
        state.blockedDates = state.blockedDates.filter(
          (b) => b.id !== action.payload
        );
      });
  },
});

export const { resetAvailabilityState } = availabilitySlice.actions;

export default availabilitySlice.reducer;
